/**
 * App-Kennung einer Person: erzeugen, ansehen, widerrufen.
 * Gehört in den Bereich der Person (Schüler, Lehrkraft, Zahlungspflichtige).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Ban, Plus, Printer } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/errors';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export function kennungCreatedMessage(data) {
  const code = data?.code || '';
  if (data?.email_sent && data?.email_to) {
    return `Kennung ${code} erstellt. Die Zugangsmail ging an ${data.email_to}.`;
  }
  if (data?.email_to) {
    return `Kennung ${code} erstellt. Die Mail an ${data.email_to} ist nicht rausgegangen.`;
  }
  return `Kennung ${code} erstellt. Keine E-Mail-Adresse hinterlegt, deshalb ging keine Mail raus.`;
}

function statusOf(row) {
  if (row.revoked) return { label: 'Widerrufen', className: 'bg-rose-50 text-rose-700 border-rose-200' };
  if (row.claimed) return { label: 'Aktiviert', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  return { label: 'Offen', className: 'bg-amber-50 text-amber-800 border-amber-200' };
}

export default function PersonAppKennungen({ personType, personId }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [printItem, setPrintItem] = useState(null);

  const load = useCallback(async () => {
    if (!personId) return;
    const res = await api.get('/admin/app-identities', {
      params: { person_type: personType, person_id: personId },
    });
    setRows(Array.isArray(res.data) ? res.data : []);
  }, [personType, personId]);

  useEffect(() => {
    load().catch((err) => {
      setRows([]);
      toast.error(formatApiError(err, 'App-Kennungen konnten nicht geladen werden'));
    });
  }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const res = await api.post('/admin/app-identities', {
        person_type: personType,
        person_id: personId,
      });
      toast.success(kennungCreatedMessage(res.data));
      setPrintItem(res.data);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, 'Kennung konnte nicht erstellt werden'));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/admin/app-identities/${id}`);
      toast.success('Kennung widerrufen');
      await load();
    } catch (err) {
      toast.error(formatApiError(err, 'Widerrufen fehlgeschlagen'));
    }
  };

  return (
    <div data-testid="person-app-kennungen">
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="text-sm text-slate-500">
          Zugang zur App für diese Person. Die Kennung wird hier erzeugt und kann hier widerrufen werden.
        </p>
        <Button type="button" size="sm" onClick={create} disabled={busy || !personId} data-testid="person-app-create">
          <Plus className="w-4 h-4 mr-1" /> Kennung erzeugen
        </Button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-slate-500">
          <tr>
            <th className="text-left py-1.5 pr-2">Kennung</th>
            <th className="text-left py-1.5 pr-2">PIN</th>
            <th className="text-left py-1.5 pr-2">Status</th>
            <th className="text-left py-1.5 pr-2">seit</th>
            <th className="text-left py-1.5 pr-2">App-Konto</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const status = statusOf(row);
            return (
              <tr key={row.id} className="border-t border-slate-100" data-testid={`person-app-row-${row.id}`}>
                <td className="py-2 pr-2 font-mono">{row.code}</td>
                <td className="py-2 pr-2 font-mono">{row.pin || '••••••'}</td>
                <td className="py-2 pr-2">
                  <Badge variant="outline" className={status.className}>{status.label}</Badge>
                </td>
                <td className="py-2 pr-2 text-slate-500">
                  {formatDate(row.claimed_at || row.created_at) || '—'}
                </td>
                <td className="py-2 pr-2 text-slate-600">
                  {row.account_email || row.account_name || '—'}
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  {!row.revoked && !row.claimed && (
                    <button
                      type="button"
                      className="p-1 text-slate-500 hover:text-slate-800"
                      onClick={() => setPrintItem(row)}
                      data-testid={`person-app-print-${row.id}`}
                      title="Drucken"
                    >
                      <Printer className="w-4 h-4" />
                    </button>
                  )}
                  {!row.revoked && (
                    <button
                      type="button"
                      className="p-1 text-rose-600 hover:text-rose-800"
                      onClick={() => revoke(row.id)}
                      data-testid={`person-app-revoke-${row.id}`}
                      title="Widerrufen"
                    >
                      <Ban className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && (
        <p className="py-6 text-center text-sm text-slate-400" data-testid="person-app-empty">
          Noch keine App-Kennung.
        </p>
      )}
      <Dialog open={!!printItem} onOpenChange={(open) => !open && setPrintItem(null)}>
        <DialogContent className="max-w-sm" data-testid="person-app-print-dialog">
          <DialogHeader>
            <DialogTitle>App-Zugang</DialogTitle>
            <DialogDescription>Kennung und PIN für die App.</DialogDescription>
          </DialogHeader>
          {printItem && (
            <div className="flex flex-col items-center gap-3 py-2">
              <QRCodeSVG value={JSON.stringify({ code: printItem.code, pin: printItem.pin })} size={140} />
              <p className="font-mono text-lg tracking-widest">{printItem.code}</p>
              <p className="font-mono text-sm text-slate-500">PIN: {printItem.pin}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
